import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '@/components/NavBar';
import MainFooter from '@/components/MainFooter';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, ExternalLink } from 'lucide-react';
import { events, Event } from '@/data/events';
import { ImageOptimizer } from '@/components/common/ImageOptimizer';
import AddToCalendarButton from '@/components/common/AddToCalendarButton';

// Helper function to check if event is over
const isEventOver = (eventDate: string, eventTime: string) => {
  const now = new Date();
  const eventDateTime = new Date(`${eventDate}T${eventTime}`);
  return now.getTime() > eventDateTime.getTime();
};

// Countdown timer component
const CountdownTimer: React.FC<{ targetDate: string; targetTime: string }> = ({ targetDate, targetTime }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [isOver, setIsOver] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const target = new Date(`${targetDate}T${targetTime}`);
      const difference = target.getTime() - now.getTime();

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
        setIsOver(false);
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setIsOver(true);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [targetDate, targetTime]);

  if (isOver) {
    return (
      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
        This event is over
      </Badge>
    );
  }

  return (
    <div className="flex items-center gap-2 text-sm text-brand-darkgrey">
      <Clock className="h-4 w-4" />
      <span className="font-medium">
        {timeLeft.days > 0 && `${timeLeft.days}d `}
        {timeLeft.hours > 0 && `${timeLeft.hours}h `}
        {timeLeft.minutes > 0 && `${timeLeft.minutes}m `}
        {timeLeft.seconds > 0 && `${timeLeft.seconds}s`}
      </span>
    </div>
  );
};

// Event card component
const EventCard: React.FC<{ event: Event; onNavigate: (path: string) => void }> = ({ event, onNavigate }) => {
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'webinar':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'event':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'workshop':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'webinar':
        return 'On-demand Webinar';
      case 'event':
        return 'On-demand Event';
      case 'workshop':
        return 'Interactive Workshop';
      default:
        return 'Event';
    }
  };

  return (
    <Card
      className="group hover:shadow-lg transition-all duration-300 border-0 bg-white cursor-pointer"
      onClick={() => onNavigate(event.ctaLink)}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onNavigate(event.ctaLink);
        }
      }}
    >
      <CardContent className="p-0">
        {/* Event Image */}
        <div className="relative h-48 bg-gradient-to-br from-brand-blue/10 to-purple-500/10 overflow-hidden rounded-t-lg">
          {event.image ? (
            <ImageOptimizer
              src={event.image}
              alt={event.title}
              className="w-full h-full object-cover"
              width={400}
              height={192}
              loading="lazy"
              objectFit="cover"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/20 to-purple-500/20" />
          )}
          
          {/* Fallback overlay when no image */}
          {!event.image && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-6xl font-bold text-brand-blue/30">
                {event.title.charAt(0)}
              </div>
            </div>
          )}
          
          {event.isLive && (
            <Badge className="absolute top-3 right-3 bg-red-500 text-white border-0 animate-pulse">
              LIVE
            </Badge>
          )}
          
          {event.specialType === 'off-the-record' && (
            <Badge className="absolute top-3 left-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 font-semibold shadow-lg">
              🔒 OFF THE RECORD
            </Badge>
          )}
        </div>

        {/* Event Content */}
        <div className="p-6">
          {/* Category Badge */}
          <Badge 
            variant="outline" 
            className={`mb-3 ${getCategoryColor(event.category)} border`}
          >
            {event.seriesName || getCategoryLabel(event.category)}
          </Badge>

          {/* Title */}
          <h3 className="text-xl font-semibold text-brand-darkgrey mb-2 group-hover:text-brand-blue transition-colors">
            {event.title}
          </h3>

          {/* Description */}
          <p className="text-brand-grey text-sm mb-4 line-clamp-2">
            {event.longDescription}
          </p>

          {/* Date and Countdown */}
          <div className="flex items-center gap-2 mb-4 text-sm text-brand-darkgrey">
            <Calendar className="h-4 w-4" />
            <span>{new Date(event.date).toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</span>
          </div>

          <CountdownTimer targetDate={event.date} targetTime={event.time} />

          {/* CTA and Add to Calendar */}
          <div className="mt-4 flex items-center gap-2">
            <Button 
              className="flex-1 bg-brand-blue hover:bg-brand-blue/90 text-white"
              onClick={(e) => {
                e.stopPropagation();
                onNavigate(event.ctaLink);
              }}
            >
              {event.ctaText}
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
            <div onClick={(e) => e.stopPropagation()}>
              <AddToCalendarButton
                title={event.title}
                description={event.longDescription}
                location="Virtual Event"
                date={event.date}
                time={event.time}
                timezone={event.timezone}
                durationMinutes={60}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Events = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    document.title = "Events - Cutshort";
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />
      
      {/* Page Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-brand-darkgrey mb-4">
              Resources
            </h1>
            <p className="text-xl text-brand-grey">
              All On-demand Events and Webinars
            </p>
          </div>
        </div>
      </div>

      {/* Events Grid */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {events.map((event) => (
            <EventCard key={event.id} event={event} onNavigate={navigate} />
          ))}
        </div>
      </div>

      <MainFooter />
    </div>
  );
};

export default Events;
